/**
 * O gate do CARTÃO FLUTUANTE do painel da conversa no `messages.vue` (§5.1;
 * kill-switch `PAINEL_PROGRESSO_CONTEXTO_ATIVO`, em `data` para os testes
 * ligarem por instância). Ligado, o cartão substitui o índice de entregas e a
 * régua na mesma casca sticky quando há ≥ 1 entrega OU plano explícito do
 * turno (getter `plano` ao vivo, ou `message.plano` na última resposta —
 * contrato `plano` da spec-008). Turno comum sem entrega não mostra cartão.
 * Com o aside aberto o cartão sai da frente (recolhido ou oculto, no próprio
 * `painel-flutuante-estado-mixin.js`).
 * Desligado, nada muda.
 *
 * ★ Fim de turno com PLANO: se o turno registrou entrega nova que abre no
 * aside, ela abre sozinha (`abrirArtefato`, a MESMA largura do clique) — também
 * quando chega DEPOIS do `chatLoading` cair (a bolha persistida registra só
 * quando o texto termina de aparecer). Só não troca a entrega que o usuário
 * escolheu DURANTE o turno (a ativa mudou desde o início dele): aside fechado,
 * ou ainda mostrando a entrega de antes (a do turno anterior), passa para a
 * nova — era o 2º `/quem-migrar` que não abria nada, com o aside preso na
 * entrega velha. "Teve plano" é decidido NA VIRADA do `chatLoading` também
 * pelo getter `plano` e pelo `message.plano` de resposta nova do turno: num
 * atalho rápido (`/quem-migrar`) o `plano`, a entrega e o `end` chegam no mesmo
 * tick, e o watcher de `planoAoVivoComItens` nunca vê o `true`. Abrir o cartão durante o turno é do próprio cartão
 * (`painel-flutuante-estado-mixin.js`).
 *
 * ★ Quem ALIMENTA o relógio da encenação (`utils/encenacao-do-plano.js`) é
 * ESTE mixin, não o cartão: o `messages.vue` vive o tempo inteiro, e o cartão só
 * monta quando a faixa ao lado do trilho já foi medida — no 1º turno depois de
 * um F5 ele nascia DEPOIS de o plano chegar, e aquele turno não era encenado
 * (o 2º em diante sim).
 * ⚠️ O gate NÃO pode ser o `chatLoading` no instante do watcher: num atalho
 * determinístico o `plano` e o `end` caem no MESMO tick, o Vue agrupa os
 * watchers e, quando o callback roda, `chatLoading` já é `false` — o 1º turno
 * ficava sem encenação por isso. Vale `turnoDaSessao`: ligado quando um turno
 * COMEÇOU aqui, desligado ao trocar de conversa (com o turno parado). Assim
 * histórico, reidratação e replay do run terminal seguem assentando na hora.
 *
 * ★ Posição: `top` e `right` são o MESMO respiro (`RESPIRO_DO_PAINEL_FLUTUANTE`).
 * O índice de mensagens mora no terço de baixo da área (`messages.vue`), então
 * o cartão não precisa desviar dele.
 *
 * Mixin só pelo tamanho do `messages.vue` (> 1000 linhas); consome dele
 * `storeModule`, `chatLoading`, `messages` e `artefatosDaConversa`.
 */
import { PAINEL_PROGRESSO_CONTEXTO_ATIVO } from '@modules/aawz-intelligence-v2/constants/Feature'
import { APRESENTACAO } from '@modules/aawz-intelligence-v2/ui-registry/apresentacao'
import { progressoDoTurno, ultimaRespostaDoAssistente } from '@modules/aawz-intelligence-v2/utils/progresso-do-turno'
import { ATRASO_DA_ABERTURA_AUTOMATICA_MS, RESPIRO_DO_PAINEL_FLUTUANTE } from '@modules/aawz-intelligence-v2/components/painel-do-chat/painel-do-chat.constantes'
import {
  encenacaoDoPlano,
  encenacaoDrenando,
  reiniciarEncenacao,
  sincronizarEncenacao,
} from '@modules/aawz-intelligence-v2/utils/encenacao-do-plano'
import { alvoDaEncenacao, assinaturaDePlano } from '@modules/aawz-intelligence-v2/utils/encenacao-do-progresso'

function temItens(plano) {
  return Boolean(plano && plano.itens && plano.itens.length)
}

export default {
  data() {
    return {
      painelProgressoContextoAtivo: PAINEL_PROGRESSO_CONTEXTO_ATIVO,
      // Entregas que já existiam quando o turno começou (identidades).
      entregasNoInicioDoTurno: [],
      // A entrega ativa no aside quando o turno começou (`null` = fechado).
      artefatoAtivoNoInicioDoTurno: null,
      // Quantas mensagens havia no início: só resposta NOVA conta como plano do turno.
      mensagensNoInicioDoTurno: 0,
      // Um turno COMEÇOU nesta conversa, nesta sessão (o que pode ser encenado).
      turnoDaSessao: false,
      turnoComPlano: false,
      aguardandoEntregaDoTurno: false,
      // Timer do respiro entre o fim da encenação e a abertura do aside.
      temporizadorDaAberturaAutomatica: null,
    }
  },
  computed: {
    // As etapas do turno (ao vivo ou da última resposta), a MESMA lista que o
    // cartão desenha — aqui ela serve ao gate do cartão e ao relógio da encenação.
    progressoDoTurnoAtual() {
      return progressoDoTurno({
        emVoo: Boolean(this.chatLoading),
        planoAoVivo: this.$store.getters[this.storeModule + '/plano'] || null,
        mensagens: this.messages,
      })
    },
    mostraPainelFlutuante() {
      if (!this.painelProgressoContextoAtivo) return false
      if (this.artefatosDaConversa.length > 0) return true
      return this.progressoDoTurnoAtual.length > 0
    },
    estiloDoPainelFlutuante() {
      const respiro = RESPIRO_DO_PAINEL_FLUTUANTE + 'px'
      return { top: respiro, right: respiro }
    },
    idDaConversaSelecionada() {
      const conversa = this.$store.getters[this.storeModule + '/selectedConversation']
      return (conversa && conversa.id) || null
    },
    // Reativo ao singleton da encenação (`encenacaoDoPlano.revelados`).
    encenacaoEmAndamento() {
      return encenacaoDoPlano.revelados < encenacaoDoPlano.alvo
    },
    planoAoVivoComItens() {
      if (!this.chatLoading) return false
      return temItens(this.$store.getters[this.storeModule + '/plano'])
    },
  },
  beforeDestroy() {
    this.limparAtrasoDaAberturaAutomatica()
  },
  watch: {
    chatLoading(emVoo, antes) {
      if (!this.painelProgressoContextoAtivo) return
      if (emVoo && !antes) {
        this.entregasNoInicioDoTurno = this.artefatosDaConversa.map((a) => a.identidade)
        this.artefatoAtivoNoInicioDoTurno = this.artefatoAtivoNoAside()
        this.mensagensNoInicioDoTurno = (this.messages || []).length
        this.turnoComPlano = false
        this.aguardandoEntregaDoTurno = false
        this.turnoDaSessao = true
        // Turno novo: o relógio da encenação não carrega o plano do anterior.
        reiniciarEncenacao()
        return
      }
      if (!emVoo && antes && this.turnoTevePlano()) {
        this.turnoComPlano = false
        this.aguardandoEntregaDoTurno = true
        this.abrirEntregaNovaDoTurno()
      }
    },
    artefatosDaConversa() {
      if (this.aguardandoEntregaDoTurno) this.abrirEntregaNovaDoTurno()
    },
    // O relógio da encenação: plano NOVO só entra na fila com turno em voo.
    progressoDoTurnoAtual: {
      immediate: true,
      handler(passos) {
        if (!this.painelProgressoContextoAtivo) return
        const lista = passos || []
        if (!lista.length) return
        const assinatura = assinaturaDePlano(lista.map((passo) => passo.titulo))
        const jaNaFila = assinatura === encenacaoDoPlano.assinatura
        if (!jaNaFila && !this.turnoDaSessao) return
        sincronizarEncenacao({ assinatura, total: lista.length, alvo: alvoDaEncenacao(lista) })
      },
    },
    // Trocar de conversa (com o turno parado) sai do "turno desta sessão": o
    // plano que chegar ali é histórico e assenta no estado final.
    idDaConversaSelecionada() {
      // ★ **[18/09/2026] A abertura automática NÃO atravessa a troca de
      //   conversa.** `abrirEntregaNovaDoTurno` sai por `if (!novas.length)
      //   return` sem desligar `aguardandoEntregaDoTurno` — e está certo
      //   DENTRO do turno: a entrega pode chegar depois (a bolha persistida só
      //   registra quando o texto termina de aparecer), então a espera
      //   continua. O defeito é a espera SOBREVIVER à conversa.
      //
      //   Com a flag ligada, abrir uma conversa do histórico dispara o watcher
      //   de `artefatosDaConversa` (as entregas dela entram ao carregar) e
      //   `entregasNoInicioDoTurno` ainda é a lista do turno ANTERIOR: tudo
      //   conta como novo e o aside abre a última entrega sozinho — o
      //   `/panorama` pulando na cara de quem só queria reler o onboarding,
      //   medido pelo owner.
      //
      //   Uma entrega pendente pertence ao turno da conversa que ficou para
      //   trás. Cancelar vem ANTES do guard de `chatLoading`: sair no meio de
      //   um turno (a view descarta o turno) não pode deixar o agendamento de
      //   pé sobre a conversa nova.
      this.aguardandoEntregaDoTurno = false
      this.limparAtrasoDaAberturaAutomatica()
      this.entregasNoInicioDoTurno = []
      this.artefatoAtivoNoInicioDoTurno = null
      if (this.chatLoading) return
      this.turnoDaSessao = false
      reiniciarEncenacao()
    },
    // A entrega abre DEPOIS de a lista de etapas terminar de aparecer (coerência
    // de exibição; a encenação tem teto de 4s). O TEXTO nunca espera.
    encenacaoEmAndamento(drenando) {
      if (!drenando && this.aguardandoEntregaDoTurno) this.abrirEntregaNovaDoTurno()
    },
    planoAoVivoComItens(tem) {
      if (tem && this.painelProgressoContextoAtivo) this.turnoComPlano = true
    },
  },
  methods: {
    turnoTevePlano() {
      if (this.turnoComPlano) return true
      if (temItens(this.$store.getters[this.storeModule + '/plano'])) return true
      const novas = (this.messages || []).slice(this.mensagensNoInicioDoTurno)
      const resposta = ultimaRespostaDoAssistente(novas)
      return Boolean(resposta && temItens(resposta.plano))
    },
    artefatoAtivoNoAside() {
      return this.$store.getters[this.storeModule + '/artefatoIdentidadeAtiva'] || null
    },
    limparAtrasoDaAberturaAutomatica() {
      if (!this.temporizadorDaAberturaAutomatica) return
      clearTimeout(this.temporizadorDaAberturaAutomatica)
      this.temporizadorDaAberturaAutomatica = null
    },
    abrirEntregaNovaDoTurno() {
      // O usuário escolheu uma entrega durante o turno: não atropela.
      const ativa = this.artefatoAtivoNoAside()
      if (ativa && ativa !== this.artefatoAtivoNoInicioDoTurno) {
        this.aguardandoEntregaDoTurno = false
        return
      }
      const antes = this.entregasNoInicioDoTurno
      if (encenacaoDrenando()) return
      // ⚠️ `inline` NÃO abre o aside sozinho — o backend pediu para mostrar o
      // cartão NA TELA (`ui.apresentacao`), e abrir o painel por cima disso
      // contradiz o próprio pedido (achado do usuário, 18/09/2026, nas etapas
      // do onboarding). O CLIQUE na linha do índice continua abrindo o aside
      // para o inline — o que muda aqui é só o automático.
      const novas = this.artefatosDaConversa.filter(
        (a) =>
          !antes.includes(a.identidade) &&
          a.parteReal &&
          a.parteReal.apresentacao !== APRESENTACAO.INLINE
      )
      if (!novas.length) return
      this.aguardandoEntregaDoTurno = false
      // Respiro depois do último check antes de o aside entrar.
      const parte = novas[novas.length - 1].parteReal
      this.limparAtrasoDaAberturaAutomatica()
      this.temporizadorDaAberturaAutomatica = setTimeout(() => {
        this.temporizadorDaAberturaAutomatica = null
        this.$store.dispatch(this.storeModule + '/abrirArtefato', parte)
      }, ATRASO_DA_ABERTURA_AUTOMATICA_MS)
    },
  },
}
